import { FormDrawer, FormDrawerContent, Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FormikErrors } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { contactService } from "../../shared/api/contactService";
import { IEmailListResource, newsletterService } from "../../shared/api/newsletterService";
import { EmailListSelectField } from "../../shared/select/EmailListSelectField";
import { MailcoachApplicationSelectField } from "../../shared/select/MailcoachApplicationSelectField";
import { ApplicationIndexResource, IContactUpdateResource } from "../../shared/types";

interface Props {
	contact: IContactUpdateResource;
	open: boolean;
	handleClose: () => void;
}

interface NewsletterDrawerFormValues {
	application: ApplicationIndexResource | null;
	emailList: IEmailListResource | null;
}

const initialValues: NewsletterDrawerFormValues = {
	application: null,
	emailList: null,
};

export const SubscribeToNewsletterDrawer: FC<Props> = ({ contact, open, handleClose }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const subscribeToNewsletter = useCallback(
		async (values: NewsletterDrawerFormValues) => {
			const response = await newsletterService.subscribe(
				values.application?.id as Id,
				values.emailList?.uuid as Id,
				contact.contactEmails.find((email) => email.isPrimary)?.email || "",
			);

			if (isSuccess(response)) {
				showToast({
					type: "success",
					body: t("Successfully subscribed to newsletter."),
					title: t("Newsletters"),
				});
				void queryClient.invalidateQueries({ queryKey: contactService.showKey(contact.id) });
				return true;
			}

			return false;
		},
		[contact.contactEmails, contact.id, queryClient, showToast, t],
	);

	const validate = useCallback(
		(values: NewsletterDrawerFormValues) => {
			const errors: FormikErrors<NewsletterDrawerFormValues> = {};

			if (!values.application) {
				errors.application = t("Required field");
			}

			if (!values.emailList) {
				errors.emailList = t("Required field");
			}

			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			title={t("Subscribe to a newsletter")}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<MailcoachApplicationSelectField name="application" label={t("Newsletter application")} />
					{props.formik.values.application?.id && (
						<EmailListSelectField
							name="emailList"
							label={t("Email list")}
							applicationId={props.formik.values.application?.id}
						/>
					)}
				</FormDrawerContent>
			)}
			initialValues={initialValues}
			open={open}
			validate={validate}
			onClose={handleClose}
			onSubmit={subscribeToNewsletter}
		/>
	);
};
